import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSwipeable } from 'react-swipeable';
import Button from '@nubank/nuds-web/components/Button/Button';

import CarouselItem from './components/CarouselItem/CarouselItem';
import {
  CarouselContainer,
  CarouselWrapper,
  ControlsContainer,
  SwipeableArea,
} from './style';

function Carousel({
  transitionDuration,
  children,
  currentStep,
  showControls,
  cardWidth,
  controlsProps,
  onStepChanged,
  onSwiped,
  autoPlay,
  renderOnlySelected,
  swipeableAreaWidth,
  swipeableAreaHeight,
}) {
  const currentIndex = currentStep - 1 || 0;
  const totalItems = React.Children.count(children);

  const isPreviousDisabled = currentIndex === 0;
  const isNextDisabled = currentIndex === totalItems - 1;

  const changeActive = offset => {
    const changed = (currentStep || 0) + offset;
    const newStep = Math.max(changed, 1);

    onStepChanged(newStep > totalItems ? totalItems : newStep);
  };

  const handlePrevChange = () => {
    changeActive(-1, totalItems);
  };

  const handleNextChange = () => {
    changeActive(1, totalItems);
  };

  const renderChild = (child, selected) => {
    if (!renderOnlySelected) {
      return child;
    }

    return selected ? child : null;
  };

  const handleAutoPlay = () => {
    if (currentStep === totalItems) {
      return onStepChanged(1);
    }

    return handleNextChange();
  };

  useEffect(() => {
    if (autoPlay && !showControls) {
      const timer = setTimeout(handleAutoPlay, autoPlay);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [currentStep, autoPlay]);

  const swipeHandles = useSwipeable({
    onSwipedLeft: () => {
      onSwiped?.();
      handleNextChange();
    },
    onSwipedRight: () => {
      onSwiped?.();
      handlePrevChange();
    },
    trackMouse: true,
  });

  return (
    <SwipeableArea
      $width={swipeableAreaWidth}
      $height={swipeableAreaHeight}
      {...swipeHandles}
    >
      <CarouselWrapper>
        <CarouselContainer
          $transitionDuration={transitionDuration}
          $transform={`${currentIndex * -100}%`}
          $shouldExpand={!showControls}
        >
          {React.Children.map(children, (child, index) => (
            <CarouselItem
              cardWidth={cardWidth}
              selected={index === currentIndex}
              aria-hidden={index !== currentIndex}
            >
              {renderChild(child, index === currentIndex)}
            </CarouselItem>
          ))}
        </CarouselContainer>
        {showControls && (
          <ControlsContainer>
            <Button
              {...controlsProps}
              onClick={handlePrevChange}
              disabled={isPreviousDisabled}
              iconProps={{ name: 'arrow-left' }}
            />
            <Button
              {...controlsProps}
              onClick={handleNextChange}
              disabled={isNextDisabled}
              iconProps={{ name: 'arrow-right' }}
            />
          </ControlsContainer>
        )}
      </CarouselWrapper>
    </SwipeableArea>
  );
}

Carousel.defaultProps = {
  transitionDuration: '0.75s',
  autoPlay: 0,
  showControls: true,
  cardWidth: 100,
  currentStep: 1,
  renderOnlySelected: false,
  onStepChanged: () => { },
  onSwiped: () => {},
  controlsProps: {
    variant: 'basic',
    styleVariant: 'black',
  },
  swipeableAreaWidth: '100%',
  swipeableAreaHeight: 'auto',
};

Carousel.propTypes = {
  autoPlay: PropTypes.number,
  cardWidth: PropTypes.number,
  children: PropTypes.node.isRequired,
  controlsProps: PropTypes.shape({
    styleVariant: PropTypes.string.isRequired,
    variant: PropTypes.string.isRequired,
  }),
  currentStep: PropTypes.number,
  onStepChanged: PropTypes.func,
  onSwiped: PropTypes.func,
  renderOnlySelected: PropTypes.bool,
  showControls: PropTypes.bool,
  swipeableAreaHeight: PropTypes.string,
  swipeableAreaWidth: PropTypes.string,
  transitionDuration: PropTypes.string,
};

export default React.memo(Carousel);
